/**
 * Business time is ALWAYS Asia/Colombo, regardless of the browser's timezone.
 * The API sends ISO-8601 timestamps with a +05:30 offset and plain `YYYY-MM-DD` business dates.
 */
export const BUSINESS_TZ = 'Asia/Colombo';

type DateInput = string | number | Date | null | undefined;

function toDate(v: DateInput): Date | null {
  if (v === null || v === undefined || v === '') return null;
  // A bare YYYY-MM-DD is a business date: anchor it at Colombo midnight so it never shifts a day.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
    return new Date(`${v}T00:00:00+05:30`);
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: BUSINESS_TZ, ...opts });

const dateFmt = fmt({ day: '2-digit', month: 'short', year: 'numeric' });
const timeFmt = fmt({ hour: '2-digit', minute: '2-digit', hour12: true });
const dateTimeFmt = fmt({
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});
const ymdFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** "03 Oct 2026" */
export function formatDate(v: DateInput): string {
  const d = toDate(v);
  return d ? dateFmt.format(d) : '—';
}

/** "02:35 pm" */
export function formatTime(v: DateInput): string {
  const d = toDate(v);
  return d ? timeFmt.format(d).toLowerCase() : '—';
}

/** "03 Oct 2026, 02:35 pm" */
export function formatDateTime(v: DateInput): string {
  const d = toDate(v);
  return d ? dateTimeFmt.format(d).replace(/\b(am|pm)\b/i, (m) => m.toLowerCase()) : '—';
}

/** Colombo calendar date as `YYYY-MM-DD` (what the API expects for date filters). */
export function toColomboYmd(v: DateInput = new Date()): string {
  const d = toDate(v);
  return d ? ymdFmt.format(d) : '';
}

/** Today in Colombo as `YYYY-MM-DD`. Never use `new Date().toISOString().slice(0,10)` (that is UTC). */
export function todayColombo(): string {
  return toColomboYmd(new Date());
}

/** Shift a `YYYY-MM-DD` string by N days (calendar arithmetic, tz-safe). */
export function addDaysYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
